import { queryOptions } from '@tanstack/react-query';
import { apiClient } from 'shared/api-client';
import * as v from 'valibot';

import { getRegisterSourceData } from '@/lib/analytics/registerSource.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { getVertex } from '@/lib/analytics/vertex.ts';
import { nullishAsOptional, paginated } from '@/lib/schema-utils.ts';

import {
  getExperimentVariant,
  getCreditScoreHistory,
  getPropertiesHistory,
  updateRegisterSource,
  type GetCreditScoreHistoryArgs,
  type GetExperimentVariantArgs,
  type GetPropertiesHistoryArgs,
} from './requests.ts';

export const authInitQuery = () =>
  queryOptions({
    queryKey: ['auth', 'init'],
    queryFn: async () => {
      const registerSource = getRegisterSourceData();

      const [resRaw] = await Promise.all([
        apiClient
          .post('brain/api/legion/', {
            json: {
              vertex: getVertex(null),
              content_type: 'init',
            },
          })
          .json(),
        updateRegisterSource(registerSource),
      ]);

      const schema = v.object({
        vertex: v.object({
          id: v.object({
            id: v.string(),
            uid: v.string(),
          }),
          user_id: nullishAsOptional(v.string()),
        }),
      });

      const res = v.parse(schema, resRaw);

      sendUserPortalEvent('landing_page', { landing_page: registerSource.url_path || '/' });

      return { userId: res.vertex.user_id };
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });

export const MeSchema = v.object({
  id: v.string(),
  created_at: v.string(),
  email: nullishAsOptional(v.string()),
  phone_number: nullishAsOptional(v.string()),
  suggested_email: nullishAsOptional(v.string()),
  analytics_user_id: v.string(),
  first_name: nullishAsOptional(v.string()),
  last_name: nullishAsOptional(v.string()),
  customerio_id: nullishAsOptional(v.string()),
  customerio_anonymous_id: nullishAsOptional(v.string()),
});

export type Me = v.InferOutput<typeof MeSchema>;

export const meQuery = () =>
  queryOptions({
    queryKey: ['me'],
    queryFn: async () => {
      const res = await apiClient.get('account/me/').json();
      return v.parse(MeSchema, res);
    },
  });

export type CheckManyConsentsResponse = {
  all_check: boolean;
  details: Record<string, boolean>;
};

export const checkManyConsentsQuery = <T extends string>(consents: T[]) =>
  queryOptions({
    queryKey: ['consents', 'check-many', { consents }],
    queryFn: async (): Promise<CheckManyConsentsResponse> => {
      const res = await apiClient
        .post('bi/consent-clusters/all_consents_check/', {
          json: { slugs: consents },
        })
        .json();

      const schema = v.record(v.picklist(consents), v.boolean());
      const data = v.parse(schema, res);

      return {
        all_check: Object.values(data).every((x) => x),
        details: data,
      };
    },
  });

const ConsentSchema = v.object({
  id: v.string(),
  created: v.string(),
  revoked: nullishAsOptional(v.string()),
  source: v.string(),
});

export type Consent = v.InferOutput<typeof ConsentSchema>;

const ConsentKindSchema = v.object({
  id: v.string(),
  slug: v.string(),
  name: v.string(),
  text_selector: v.string(),
  text: nullishAsOptional(v.string()),
  consents: v.array(ConsentSchema),
  created: v.string(),
});

export type ConsentKind = v.InferOutput<typeof ConsentKindSchema>;

export const getConsentsQuery = () =>
  queryOptions({
    queryKey: ['consents', 'all'],
    queryFn: async () => {
      const res = await apiClient.get('bi/consent/', { searchParams: { per_page: 50 } }).json();
      const schema = paginated(ConsentKindSchema);
      return v.parse(schema, res);
    },
  });

export const getExperimentVariantQuery = (args: GetExperimentVariantArgs) =>
  queryOptions({
    queryKey: ['experiments', args],
    queryFn: () => getExperimentVariant(args),
  });

export const getPropertiesHistoryQuery = ({ page = 1 }: GetPropertiesHistoryArgs = {}) =>
  queryOptions({
    queryKey: ['properties', 'history', { page }],
    queryFn: () => getPropertiesHistory({ page }),
  });

export const getCreditScoreHistoryQuery = ({ page = 1 }: GetCreditScoreHistoryArgs = {}) =>
  queryOptions({
    queryKey: ['creditscore', 'history', { page }],
    queryFn: () => getCreditScoreHistory({ page }),
  });

const AlertSchema = v.object({
  enabled: v.boolean(),
  message: v.string(),
});

export type Alert = v.InferOutput<typeof AlertSchema>;

export const getAlertQuery = () =>
  queryOptions({
    queryKey: ['agent-portal', 'alert'],
    queryFn: async () => {
      const res = await apiClient.get('kv_store/api/data/alert/').json();
      return v.parse(AlertSchema, res);
    },
  });
